import { HttpErrorResponse } from '@angular/common/http';
import { UNKNOWN_ERROR_MESSAGE, VALIDATION_MESSAGE, genericMessageForStatus } from './generic-error-copy';

/**
 * Turns an API error body into the sentence a caller (and usually a user) sees.
 *
 * ## Which keys the frontend surfaces
 *
 * VisionApi does not speak one error shape, so this file is the adapter. Only these are lifted:
 *
 * | Body                         | Result                                    |
 * | ---------------------------- | ----------------------------------------- |
 * | a plain string               | that string, if it passes the guards      |
 * | `{ error: "..." }`           | that string, if it passes the guards      |
 * | ProblemDetails `{ detail }`  | the detail, if it passes the guards       |
 * | a validation body            | a fixed sentence (the field messages are NOT surfaced) |
 * | anything else                | `''`, and the caller falls back to `genericMessageForStatus` |
 *
 * `message` and `title` are deliberately NOT lifted:
 *
 * - `message` is the key a developer reaches for when writing `BadRequest(new { message = ex.Message })`.
 *   Lifting it globally would display every such site, present and future; `SqlException` and
 *   `DbUpdateException` messages carry table, column and constraint names and offending key values.
 * - `title` is what ASP.NET stamps on a bare `NotFound()` ("Not Found"), which would read as curated
 *   copy while being nothing of the kind. No endpoint writes a `title` by hand.
 *
 * **A new API error shape must use `error` or ProblemDetails `detail`, or add a rung here.** The
 * producer-side half of this rule lives in `api/CLAUDE.md` under "Error responses" — that is where a
 * C# author will read it.
 *
 * ## Which export a component may use
 *
 * `messageFromCaughtError`, and only that one. Components must NOT import `extractServerErrorMessage`
 * or `resolveServerErrorMessage`. After the
 * interceptor runs, a handler already receives a plain string; re-parsing `{error}`/`detail` in a
 * component re-imports the knowledge this file exists to centralise. Those two are for the interceptor
 * and for genuine `PRESERVE_ERROR_RESPONSE` opt-outs only.
 *
 * `messageFromCaughtError` is the component-facing one: it turns whatever a handler was actually
 * handed into a sentence, without the caller needing to know any of the above.
 *
 * The result must never be bound to `[innerHTML]`. It is server-supplied text.
 */

/** Longest lifted message we will hand back for display. Beyond this it is abridged, not dropped. */
// Read out of this file by `FrontendErrorContract` in the API test project, which several API contract
// tests use to check that the messages they pin fit the space they are shown in — for example, that a
// message written in SQL cannot outgrow it. Renaming this constant, or computing it rather than writing
// it as a literal, fails those tests — with a message saying so.
const MAX_DISPLAY_LENGTH = 300;

/** Shortest acceptable result of a word-boundary cut before we give up and cut mid-word instead. */
const MIN_WORD_BOUNDARY_LENGTH = 200;

/**
 * Above this a candidate is not a message at all — an error page, a serialized object, a dump — so it
 * is rejected outright rather than abridged. A different question from MAX_DISPLAY_LENGTH, which asks
 * how much of a genuine message fits on screen. Both run, this one first.
 */
const MAX_BULK_LENGTH = 2000;

/**
 * Markup anywhere in the candidate, not just at the start. An error page rarely opens with `<html>`:
 * it may lead with `<?xml`, a byte-order mark, a comment, or the prose "Server Error in '/'
 * Application.", and a WAF page with `<div`. Searching the whole string rather than anchoring to
 * position zero is what catches those — a leading BOM or a line of prose is caught once the page's
 * first tag appears, which every such page has.
 *
 * Known false positive, accepted: prose containing a less-than sign ("Start date < end date
 * required.") is rejected and degrades to the generic sentence. No curated message in the API
 * contains one today, and the degradation is safe.
 */
const MARKUP_PATTERN = /<\s*[a-z!?/]/i;

/**
 * Best-effort filter for text that is obviously a leaked exception rather than a curated message.
 *
 * DELIBERATELY NOT EXHAUSTIVE, and NOT a security boundary: a leaked exception sentence such as
 * "Violation of PRIMARY KEY constraint 'PK_TRAINEE'..." is plain prose and passes straight through.
 * The real control is source-side, in the API. `ExceptionHandlerMiddleware` never puts an exception
 * message on the wire, and since #1762 a catch block returns a fixed sentence instead of the
 * exception's text: `ExceptionTextContractTests` fails the build when a caught exception's message
 * reaches an `ErrorMessage`, a `return` or a response helper. A plain-string body is still surfaced
 * as-is, so this list is not a defence on its own: a bare `SqlException.Message` passes it. Do NOT
 * extend this list in place of fixing the endpoint that leaked.
 */
const LEAK_MARKERS = ['Microsoft.Data.SqlClient', 'System.Data', 'Stack trace', '   at ', 'Server=', 'Data Source='];

// `unknown` / `Record<string, unknown>` throughout these helpers: they operate on the opaque HTTP
// response body, the true-boundary case the standards allow. See `extractServerErrorMessage`.
function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isNonEmptyStringArray(value: unknown): boolean {
  return Array.isArray(value) && value.length > 0 && value.every((item) => typeof item === 'string');
}

/**
 * A validation body, in either shape the API produces: `ValidationProblemDetails` (`{ errors: {...} }`)
 * or the bare `SerializableError` dictionary that `BadRequest(ModelState)` returns.
 *
 * The at-least-one-key test is load-bearing: `Object.values({}).every(...)` and
 * `Object.values([]).every(...)` are both vacuously true, which would tell a user their input was
 * invalid on an empty body.
 */
function isValidationBody(body: Record<string, unknown>): boolean {
  const keys = Object.keys(body);
  if (keys.length === 0) {
    return false;
  }
  if (isPlainObject(body['errors']) && Object.keys(body['errors']).length > 0) {
    return true;
  }
  // The bare `SerializableError` from `BadRequest(ModelState)` is keyed by the model-binding path,
  // which is a C# property name (`Email`) or the empty string for a model-level error. Requiring that
  // no key starts with a lowercase letter is what stops an unrelated body of string arrays —
  // `{ warnings: [...] }` — being announced to a user as "some of the details were not valid", which
  // would simply be false.
  //
  // The rung is a heuristic and leaky in both directions.
  //
  // Two accepted MISSES, which degrade safely to the generic sentence rather than showing anything
  // wrong. First, a nested binding path (`filter.Name`, from a complex `[FromQuery]` parameter) is
  // lowercase and so rejected. Second, any body whose keys the serialiser has camel-cased would be
  // rejected wholesale — that cannot happen today, because controller results use System.Text.Json
  // with `PropertyNamingPolicy = CamelCase` but `DictionaryKeyPolicy` unset (`Startup.cs`), and that
  // policy does not touch dictionary keys. A serialiser change would silently disable this rung,
  // which is why it is written down here rather than left as folklore.
  //
  // One accepted FALSE POSITIVE: a non-validation body whose keys happen to be capitalised
  // (`{ Warnings: ['…'] }`) would be announced to a user as invalid input. No endpoint emits one
  // today, and the alternative — an allow-list of model property names — is not maintainable.
  return keys.every((key) => !/^[a-z]/.test(key) && isNonEmptyStringArray(body[key]));
}

/**
 * Rejects a lifted candidate that is not usable prose. A rejected candidate does NOT end resolution —
 * the caller continues to the next rung, so `{ error: '<html>...', detail: 'Real reason.' }` yields
 * the detail.
 */
function isUnusable(candidate: string): boolean {
  return (
    candidate.trim() === '' ||
    candidate.length > MAX_BULK_LENGTH ||
    MARKUP_PATTERN.test(candidate) ||
    LEAK_MARKERS.some((marker) => candidate.includes(marker))
  );
}

/**
 * Abridges a message that is too long to display, rather than discarding it: a truncated real reason
 * beats a generic sentence. Cuts on a word boundary, unless that would throw away most of the
 * message, in which case it cuts mid-word.
 *
 * The ellipsis counts inside the limit, so the result is never longer than MAX_DISPLAY_LENGTH.
 */
function truncateForDisplay(message: string): string {
  if (message.length <= MAX_DISPLAY_LENGTH) {
    return message;
  }
  // Enough to know the message was abridged, without republishing the tail into a browser console
  // that screen-shares and extensions can capture — an over-long body is disproportionately likely to
  // be a validation echo carrying submitted personal data.
  console.error(`Server error message abridged for display (${message.length} characters)`);

  const room = MAX_DISPLAY_LENGTH - 1;
  const lastSpace = message.lastIndexOf(' ', room);
  const cut = lastSpace >= MIN_WORD_BOUNDARY_LENGTH ? lastSpace : room;
  // `.replace` rather than `.trimEnd()`: the project's TypeScript lib target predates it.
  return `${message.slice(0, cut).replace(/\s+$/, '')}…`;
}

/**
 * Reads the message out of an API error body, or returns `''` when there is nothing usable.
 *
 * `body: unknown` because this genuinely is the opaque boundary the standards allow: every request in
 * the app passes through here and the body's shape is only known at runtime.
 */
export function extractServerErrorMessage(body: unknown): string {
  return liftServerErrorMessage(body)?.message ?? '';
}

/**
 * Where a displayed message came from:
 * - `'server'` — the API's own words, lifted from the body;
 * - `'validation'` — the body was a validation response, whose field messages are never shown (they
 *   echo what was submitted), so the sentence is OUR fixed copy for that case;
 * - `'generic'` — nothing usable in the body; the sentence is our fallback for the status.
 *
 * Decided by which branch produced the message, never by comparing text: the 403 sentence is
 * deliberately identical to the API's own, so a text comparison would misreport every genuine 403.
 */
export type ServerErrorSource = 'server' | 'validation' | 'generic';

/**
 * The lifting rungs, reporting which one matched. `null` means nothing usable was found.
 */
function liftServerErrorMessage(
  body: unknown
): { message: string; source: Exclude<ServerErrorSource, 'generic'> } | null {
  // A plain-string body is the shape ~200 endpoints use, and the one path that already worked before
  // this file existed. It gets the SAME guards as a lifted candidate, because it is also the shape an
  // IIS or WAF error page arrives in when a response is not JSON — unguarded, that dumps a whole HTML
  // document into a snackbar. Measured against `api/` when this landed: the longest string body in the
  // codebase is 138 characters and none contains markup, so no curated message is affected.
  if (typeof body === 'string') {
    return isUnusable(body) ? null : { message: truncateForDisplay(body), source: 'server' };
  }

  if (!isPlainObject(body)) {
    return null;
  }

  const candidates: string[] = [];
  if (typeof body['error'] === 'string') {
    candidates.push(body['error']);
  }
  if (typeof body['detail'] === 'string') {
    candidates.push(body['detail']);
  }

  for (const candidate of candidates) {
    if (!isUnusable(candidate)) {
      return { message: truncateForDisplay(candidate), source: 'server' };
    }
  }

  // The field messages themselves are not surfaced: their values echo whatever was submitted (which
  // may be personal data), their keys are C# property names, and their order is the server's. The
  // sentence shown is ours, and is reported as such so the interceptor's diagnostic still fires.
  return isValidationBody(body) ? { message: VALIDATION_MESSAGE, source: 'validation' } : null;
}

/** What to show a user, and where the words came from. */
export interface ResolvedServerError {
  /** The message to display. Never empty. */
  message: string;
  /** Which branch produced the message — see {@link ServerErrorSource}. */
  source: ServerErrorSource;
}

/**
 * The single entry point. Returns the server's own explanation where there is one, and a plain-English
 * sentence where there is not.
 *
 * Reports `source` rather than leaving a caller to work it out. The obvious alternative — comparing
 * the result against `genericMessageForStatus` — is wrong: the 403 sentence is deliberately identical
 * to the API's own, so a genuine 403 would be misreported as "nothing was surfaced".
 */
export function resolveServerErrorMessage(err: HttpErrorResponse): ResolvedServerError {
  const lifted = liftServerErrorMessage(err.error);
  return lifted === null ? { message: genericMessageForStatus(err.status), source: 'generic' } : lifted;
}

/**
 * The string to show for whatever an error callback was actually handed.
 *
 * Handlers reach for `'Could not do the thing — ' + error`, and that is *almost* right: for the
 * statuses the interceptor rewrites, `error` really is the resolved message. But statuses 0, 401 and
 * 422 are passed through untouched, so those same handlers can receive a whole `HttpErrorResponse` —
 * and concatenating one yields the literal `[object Object]`, because `HttpErrorResponse` defines no
 * `toString`. A dropped connection is the reachable case, and it is the one a user hits most.
 *
 * This exists so that decision lives in one reviewed place instead of a `typeof` ternary copied into
 * every handler — which is the duplication #1634 is trying to delete, not add to.
 *
 * For an `HttpErrorResponse` this delegates to the same guards as the interceptor, so the accepted
 * false positive documented on `MARKUP_PATTERN` applies here too: a curated message containing a
 * less-than sign degrades to a generic sentence rather than being shown.
 */
export function messageFromCaughtError(error: unknown): string {
  if (typeof error === 'string') {
    // `.trim()`, matching `isUnusable`: a whitespace-only string would otherwise reach a snackbar as a
    // blank message. The interceptor never emits one, so this is defence in depth for a hand-thrown
    // value rather than a live path.
    return error.trim() === '' ? UNKNOWN_ERROR_MESSAGE : error;
  }
  if (error instanceof HttpErrorResponse) {
    return resolveServerErrorMessage(error).message;
  }
  return UNKNOWN_ERROR_MESSAGE;
}
